import { UIModel } from '../types/model.types';
import { Group } from '../types/group.types';
import { Tab } from '../types/tab.types';
import { Container } from '../types/canvas.types';

export class MockApiService {
  private uiModels: UIModel[] = [];
  private currentModelId: string | null = null;

  constructor() {
    const initialModel = this.createInitialModel();
    this.uiModels.push(initialModel);
    this.currentModelId = initialModel._id;
  }

  /**
   * Create a new UI Model
   */
  async createUIModel(name: string): Promise<string> {
    const newModel: UIModel = {
      _id: `model-${Date.now()}`,
      name,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'current-user',
      layout: {
        header: {
          showHeader: true,
          showTitle: true,
          titleText: name,
          titleLayout: 'Centre',
          logoUrl: '',
          showResetButton: true,
          resetButtonText: 'Reset',
          resetButtonShowIcon: false,
          resetButtonShowTooltip: false,
          showCloseButton: true,
          closeButtonText: 'Close',
          closeButtonShowIcon: false,
          closeButtonShowTooltip: false
        },
        footer: {
          showFooter: true,
          showDescription: true,
          descriptionText: 'Footer description or disclaimer text',
          showPreviousButton: true,
          previousButtonText: 'Previous',
          previousButtonShowIcon: false,
          previousButtonShowTooltip: false,
          previousButtonTooltipText: '',
          showNextButton: true,
          nextButtonText: 'Next',
          nextButtonShowIcon: false,
          nextButtonShowTooltip: false,
          nextButtonTooltipText: '',
          previousButtonIconUrl: '',
          nextButtonIconUrl: ''
        },
        groups: [
          {
            id: `group-${Date.now()}`,
            name: 'Group 1',
            order: 0,
            orientation: 'horizontal',
            configuration: {
                showTitle: true,
            },
            pages: [
              {
                id: `tab-${Date.now()}`,
                name: 'Page 1',
                order: 0,
                configuration: {
                  autoAdvance: false,
                },
                canvas: { containers: [] }
              }
            ]
          }
        ]
      },
      theme: {
        colors: {
          primary: '#1a73e8',
          secondary: '#f1f3f4',
          background: '#ffffff',
          text: '#202124'
        },
        typography: {
          fontFamily: 'Roboto, sans-serif',
          fontSize: {
            base: '14px',
            heading: '24px',
            subheading: '18px'
          }
        },
        spacing: {
          unit: '8px',
          container: '16px',
          element: '12px'
        }
      },
      customCSS: {},
      editorState: {
        activeGroupId: null,
        activePageId: null,
        selectedContainerId: null,
        selectedFieldId: null, 
        selectedSubmodelId: null,
        fieldConfigurations: {},
        selectedFieldType: null,
        selectedFieldParentId: null,
        selectedWidgetId: null,
        selectedWidgetType: null,
        componentUsage: {},
        componentRegistry: {
          subModels: {},
          widgets: {}
        },
        selectionPath: []
      },
      

    };

    this.uiModels.push(newModel);
    this.currentModelId = newModel._id;
    return newModel._id;
  }

  /**
   * Get all UI Models
   */
  async getUIModels(): Promise<UIModel[]> {
    return this.uiModels;
  }

  /**
   * Get a specific UI Model by ID
   */
  async getUIModel(modelId: string): Promise<UIModel | null> {
    const model = this.uiModels.find(m => m._id === modelId);
    return model || null;
  }

  /**
   * Update a UI Model
   */
  async updateUIModel(modelId: string, updates: Partial<UIModel>): Promise<boolean> {
    const modelIndex = this.uiModels.findIndex(m => m._id === modelId);
    if (modelIndex === -1) return false;

    this.uiModels[modelIndex] = {
      ...this.uiModels[modelIndex],
      ...updates,
      updatedAt: new Date().toISOString()
    };

    return true;
  }

  /**
   * Delete a UI Model
   */
  async deleteUIModel(modelId: string): Promise<boolean> {
    const modelIndex = this.uiModels.findIndex(m => m._id === modelId);
    if (modelIndex === -1) return false;

    this.uiModels.splice(modelIndex, 1);
    if (this.currentModelId === modelId) {
      this.currentModelId = this.uiModels.length > 0 ? this.uiModels[0]._id : null;
    }

    return true;
  }

  /**
   * Get current model ID
   */
  getCurrentModelId(): string | null {
    return this.currentModelId;
  }

  /**
   * Set current model ID
   */
  setCurrentModelId(modelId: string): void {
    this.currentModelId = modelId;
  }

  /**
   * Get all groups for a UI Model
   */
  async getGroups(modelId: string): Promise<Group[]> {
    const model = await this.getUIModel(modelId);
    if (!model) return [];
    
    return model.layout.groups;
  }

  /**
   * Add a group to a UI Model
   */
  async addGroup(modelId: string, group: Group): Promise<boolean> {
    const model = await this.getUIModel(modelId);
    if (!model) return false;

    model.layout.groups.push(group);
    model.updatedAt = new Date().toISOString();
    return true;
  }

  /**
   * Update a group in a UI Model
   */
  async updateGroup(modelId: string, groupId: string, updates: Partial<Group>): Promise<boolean> {
    const model = await this.getUIModel(modelId);
    if (!model) return false;

    const groupIndex = model.layout.groups.findIndex(g => g.id === groupId);
    if (groupIndex === -1) return false;

    model.layout.groups[groupIndex] = {
      ...model.layout.groups[groupIndex],
      ...updates
    };

    model.updatedAt = new Date().toISOString();
    return true;
  }

  /**
   * Delete a group from a UI Model
   */
  async deleteGroup(modelId: string, groupId: string): Promise<boolean> {
    const model = await this.getUIModel(modelId);
    if (!model) return false;

    const groupIndex = model.layout.groups.findIndex(g => g.id === groupId);
    if (groupIndex === -1) return false;

    model.layout.groups.splice(groupIndex, 1);
    model.updatedAt = new Date().toISOString();
    return true;
  }

  /**
   * Get all pages for a group
   */
  async getTabs(modelId: string, groupId: string): Promise<Tab[]> {
    const model = await this.getUIModel(modelId);
    if (!model) return [];

    const group = model.layout.groups.find(g => g.id === groupId);
    if (!group) return [];

    return group.pages;
  }

  /**
   * Add a tab to a group
   */
  async addTab(modelId: string, groupId: string, tab: Tab): Promise<boolean> {
    const model = await this.getUIModel(modelId);
    if (!model) return false;

    const group = model.layout.groups.find(g => g.id === groupId);
    if (!group) return false;

    group.pages.push(tab);
    model.updatedAt = new Date().toISOString();
    return true;
  }

  /**
   * Update a tab in a group
   */
  async updateTab(modelId: string, groupId: string, pageId: string, updates: Partial<Tab>): Promise<boolean> {
    const model = await this.getUIModel(modelId);
    if (!model) return false;

    const group = model.layout.groups.find(g => g.id === groupId);
    if (!group) return false;

    const tabIndex = group.pages.findIndex(t => t.id === pageId);
    if (tabIndex === -1) return false;

    group.pages[tabIndex] = {
      ...group.pages[tabIndex],
      ...updates
    };

    model.updatedAt = new Date().toISOString();
    return true;
  }

  /**
   * Delete a tab from a group
   */
  async deleteTab(modelId: string, groupId: string, pageId: string): Promise<boolean> {
    const model = await this.getUIModel(modelId);
    if (!model) return false;

    const group = model.layout.groups.find(g => g.id === groupId);
    if (!group) return false;

    const tabIndex = group.pages.findIndex(t => t.id === pageId);
    if (tabIndex === -1) return false;

    group.pages.splice(tabIndex, 1);
    model.updatedAt = new Date().toISOString();
    return true;
  }

  /**
   * Get all containers for a tab
   */
  async getContainers(modelId: string, groupId: string, pageId: string): Promise<Container[]> {
    const model = await this.getUIModel(modelId);
    if (!model) return [];

    const group = model.layout.groups.find(g => g.id === groupId);
    if (!group) return [];

    const tab = group.pages.find(t => t.id === pageId);
    if (!tab) return [];

    return tab.canvas.containers;
  }

  /**
   * Add a container to a tab
   */
  async addContainer(modelId: string, groupId: string, pageId: string, container: Container): Promise<boolean> {
    const model = await this.getUIModel(modelId);
    if (!model) return false;

    const group = model.layout.groups.find(g => g.id === groupId);
    if (!group) return false;

    const tab = group.pages.find(t => t.id === pageId);
    if (!tab) return false;

    tab.canvas.containers.push(container);
    model.updatedAt = new Date().toISOString();
    return true;
  }

  /**
   * Update a container in a tab
   */
  async updateContainer(
    modelId: string, 
    groupId: string, 
    pageId: string, 
    containerId: string, 
    updates: Partial<Container>
  ): Promise<boolean> {
    const model = await this.getUIModel(modelId);
    if (!model) return false;

    const group = model.layout.groups.find(g => g.id === groupId);
    if (!group) return false;

    const tab = group.pages.find(t => t.id === pageId);
    if (!tab) return false;

    const containerIndex = tab.canvas.containers.findIndex(c => c.id === containerId);
    if (containerIndex === -1) return false;

    tab.canvas.containers[containerIndex] = {
      ...tab.canvas.containers[containerIndex],
      ...updates
    };

    model.updatedAt = new Date().toISOString();
    return true;
  }

  /**
   * Delete a container from a tab
   */
  async deleteContainer(modelId: string, groupId: string, pageId: string, containerId: string): Promise<boolean> {
    const model = await this.getUIModel(modelId);
    if (!model) return false;

    const group = model.layout.groups.find(g => g.id === groupId);
    if (!group) return false;

    const tab = group.pages.find(t => t.id === pageId);
    if (!tab) return false;

    const containerIndex = tab.canvas.containers.findIndex(c => c.id === containerId);
    if (containerIndex === -1) return false;

    tab.canvas.containers.splice(containerIndex, 1);
    model.updatedAt = new Date().toISOString();
    return true;
  }

  /**
   * Create an initial UI Model with some sample data
   */
  private createInitialModel(): UIModel {
    return {
      _id: `model-${Date.now()}`,
      name: 'Sample UI Model',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'current-user',
      layout: {
        header: {
          showHeader: true,
          showTitle: true,
          titleText: 'Sample UI Model',
          titleLayout: 'Centre',
          logoUrl: '',
          showResetButton: true,
          resetButtonText: 'Reset',
          resetButtonShowIcon: false,
          resetButtonShowTooltip: false,
          showCloseButton: true,
          closeButtonText: 'Close',
          closeButtonShowIcon: false,
          closeButtonShowTooltip: false
        },
        footer: {
          showFooter: true,
          showDescription: true,
          descriptionText: 'Footer description or disclaimer text',
          showPreviousButton: true,
          previousButtonText: 'Previous',
          previousButtonShowIcon: false,
          previousButtonShowTooltip: false,
          previousButtonTooltipText: '',
          showNextButton: true,
          nextButtonText: 'Next',
          nextButtonShowIcon: false,
          nextButtonShowTooltip: false,
          nextButtonTooltipText: '',
          previousButtonIconUrl: '',
          nextButtonIconUrl: ''
        },
        groups: [
          {
            id: 'group-1',
            name: 'Group 1',
            order: 0,
            orientation: 'horizontal',
            configuration: {
                showTitle: true,
            },
            pages: [
              {
                id: 'tab-1',
                name: 'Page 1',
                order: 0,
                configuration: {
                  autoAdvance: false,
                },
                canvas: {
                  containers: [
                    {
                      id: 'container-1',
                      name: 'Container 1',
                      order: 0,
                      configuration: {
                        width: '100%',
                        height: 'auto',
                        alignment: 'left',
                        layout: 'vertical',
                        showTitle: true
                      },
                      content: {
                        type: 'widget',
                        componentId: 'empty',
                        'widget-type': 'contentBlock',
                        nestedContainers: []
                      },
                      usageInfo: {
                        usedComponents: []
                      }
                    }
                  ]
                }
              },
              {
                id: 'tab-2',
                name: 'Page 2',
                order: 1,
                configuration: {
                  autoAdvance: false,
                },
                canvas: {
                  containers: [
                    {
                      id: 'container-2',
                      name: 'Container 1',
                      order: 0,
                      configuration: {
                        width: '100%',
                        height: 'auto',
                        alignment: 'left',
                        layout: 'vertical',
                        showTitle: true
                      },
                      content: {
                        type: 'widget',
                        componentId: 'empty',
                        'widget-type': 'contentBlock',
                        nestedContainers: []
                      },
                      usageInfo: {
                        usedComponents: []
                      }
                    }
                  ]
                }
              }
            ]
          }
        ]
      },
      theme: {
        colors: {
          primary: '#1a73e8',
          secondary: '#f1f3f4',
          background: '#ffffff',
          text: '#202124'
        },
        typography: {
          fontFamily: 'Roboto, sans-serif',
          fontSize: {
            base: '14px',
            heading: '24px',
            subheading: '18px'
          }
        },
        spacing: {
          unit: '8px',
          container: '16px',
          element: '12px'
        }
      },
      customCSS: {},
      editorState: {
        activeGroupId: null,
        activePageId: null,
        selectedContainerId: null,
        selectedFieldId: null,  
        selectedFieldType: null,
        selectedFieldParentId: null,
        selectedSubmodelId: null,
        selectedWidgetId: null,
        selectedWidgetType: null,
        fieldConfigurations: {},
        componentUsage: {},
        componentRegistry: {
          subModels: {},
          widgets: {}
        },
        selectionPath: []
      }
    };
  }
}

// Export a singleton instance
export const mockApiService = new MockApiService();